import { SeededRandom } from './SeededRandom';
import type { HeightMap, Point, WorldGeneratorConfig } from './types';

export class TerrainGenerator {
  private readonly random: SeededRandom;

  constructor(private readonly config: WorldGeneratorConfig) {
    this.random = new SeededRandom(config.seed.seed).fork('terrain');
  }

  generateHeightMap(): HeightMap {
    const values: number[] = [];
    for (let y = 0; y < this.config.height; y++) {
      for (let x = 0; x < this.config.width; x++) {
        values.push(this.heightAt({ x, y }));
      }
    }

    return {
      width: this.config.width,
      height: this.config.height,
      values,
    };
  }

  private heightAt(point: Point): number {
    const nx = point.x / Math.max(1, this.config.width - 1);
    const ny = point.y / Math.max(1, this.config.height - 1);
    const mountainRidge = ridge(nx, ny, 0.78, 0.18, 0.22) * 0.46;
    const southWestHill = ridge(nx, ny, 0.16, 0.9, 0.18) * 0.28;
    const lakeBasin = ridge(nx, ny, 0.12, 0.72, 0.2) * -0.34;
    const riverValley = riverDistance(point) * -0.18;
    const subtleNoise = (this.randomForPoint(point) - 0.5) * 0.08;
    return clamp(0.08, 0.95, 0.46 + mountainRidge + southWestHill + lakeBasin + riverValley + subtleNoise);
  }

  private randomForPoint(point: Point): number {
    const local = new SeededRandom(this.config.seed.seed ^ (point.x * 73856093) ^ (point.y * 19349663));
    return (local.next() + this.random.next() * 0.25) / 1.25;
  }
}

function riverDistance(point: Point): number {
  const spine: readonly Point[] = [
    { x: 26, y: 3 },
    { x: 25, y: 6 },
    { x: 24, y: 8 },
    { x: 25, y: 10 },
    { x: 24, y: 12 },
    { x: 22, y: 16 },
    { x: 20, y: 19 },
  ];
  const distance = Math.min(...spine.map((candidate) => Math.hypot(point.x - candidate.x, point.y - candidate.y)));
  return Math.max(0, 1 - distance / 3.5);
}

function ridge(x: number, y: number, cx: number, cy: number, radius: number): number {
  const distance = Math.hypot(x - cx, y - cy);
  return Math.max(0, 1 - distance / radius);
}

function clamp(min: number, max: number, value: number): number {
  return Math.max(min, Math.min(max, value));
}
